import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";
import AdminRoyalties from "./AdminRoyalties";
import {
  getFinanceLedger,
  getFinanceLedgerDetail,
  getFinancePayoutStatement,
  getFinanceReconciliation,
  type FinanceLedgerItem,
  type FinanceLedgerKind,
  type FinancePayoutStatement,
  type FinanceReconciliationIssue,
} from "../api/adminFinanceApi";
import {
  approveCreatorPayout,
  createCreatorPayout,
  getCreatorPayouts,
  getOutstandingCreatorPayouts,
  markCreatorPayoutPaid,
  rejectCreatorPayout,
  type CreatorPayoutPreview,
  type CreatorPayoutRecord,
} from "../api/adminPayoutsApi";
import InfoMarkerHint from "../Components/ui/InfoMarkerHint";

type FinanceTab = "sales-ledger" | "token-ledger" | "creator-accruals" | "payout-queue" | "reconciliation";

const TAB_LIST: Array<{ key: FinanceTab; label: string }> = [
  { key: "sales-ledger", label: "Satış Defteri" },
  { key: "token-ledger", label: "Token Defteri" },
  { key: "creator-accruals", label: "Yayıncı Tahakkukları" },
  { key: "payout-queue", label: "Ödeme Kuyruğu" },
  { key: "reconciliation", label: "Mutabakat" },
];

const STATUS_BADGE: Record<string, string> = {
  pending: "border-amber-300 bg-amber-50 text-amber-800",
  approved: "border-blue-300 bg-blue-50 text-blue-800",
  rejected: "border-rose-300 bg-rose-50 text-rose-800",
  paid: "border-emerald-300 bg-emerald-50 text-emerald-800",
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export default function AdminFinance() {
  const [params, setParams] = useSearchParams();
  const [from, setFrom] = useState(() => dayjs().subtract(30, "day").format("YYYY-MM-DD"));
  const [to, setTo] = useState(() => dayjs().format("YYYY-MM-DD"));

  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerItems, setLedgerItems] = useState<FinanceLedgerItem[]>([]);
  const [ledgerDetail, setLedgerDetail] = useState<any | null>(null);
  const [ledgerDetailLoadingId, setLedgerDetailLoadingId] = useState<string | null>(null);

  const [reconLoading, setReconLoading] = useState(false);
  const [reconItems, setReconItems] = useState<FinanceReconciliationIssue[]>([]);

  const [outstanding, setOutstanding] = useState<CreatorPayoutPreview[]>([]);
  const [records, setRecords] = useState<CreatorPayoutRecord[]>([]);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [focusPurchaseId, setFocusPurchaseId] = useState<string | null>(null);
  const [focusPayoutId, setFocusPayoutId] = useState<string | null>(null);

  const tab = useMemo<FinanceTab>(() => {
    const raw = (params.get("tab") ?? "sales-ledger").toLowerCase();
    if (TAB_LIST.some((item) => item.key === raw)) {
      return raw as FinanceTab;
    }
    return "sales-ledger";
  }, [params]);

  const activeLabel = useMemo(() => TAB_LIST.find((item) => item.key === tab)?.label ?? "Finans", [tab]);

  const selectTab = (nextTab: FinanceTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", nextTab);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    setFocusPurchaseId(params.get("focusPurchase"));
    setFocusPayoutId(params.get("focusPayout"));
  }, [params]);

  const loadLedger = useCallback(async (kind: FinanceLedgerKind) => {
    setLedgerLoading(true);
    const response = await getFinanceLedger({
      kind,
      start: from,
      end: to,
      take: 300,
      skip: 0,
    });
    setLedgerItems(response.items);
    setLedgerLoading(false);
  }, [from, to]);

  const loadReconciliation = useCallback(async () => {
    setReconLoading(true);
    const response = await getFinanceReconciliation(200);
    setReconItems(response.items);
    setReconLoading(false);
  }, []);

  const loadPayouts = useCallback(async () => {
    setPayoutLoading(true);
    const [outstandingResponse, recordsResponse] = await Promise.all([
      getOutstandingCreatorPayouts(from, to),
      getCreatorPayouts(),
    ]);
    setOutstanding(outstandingResponse);
    setRecords(recordsResponse);
    setPayoutLoading(false);
  }, [from, to]);

  useEffect(() => {
    if (tab === "sales-ledger") {
      void loadLedger("sales");
      return;
    }
    if (tab === "token-ledger") {
      void loadLedger("token");
      return;
    }
    if (tab === "reconciliation") {
      void loadReconciliation();
      return;
    }
    if (tab === "payout-queue") {
      void loadPayouts();
    }
  }, [tab, loadLedger, loadReconciliation, loadPayouts]);

  useEffect(() => {
    if (tab !== "sales-ledger" || !focusPurchaseId || ledgerItems.length === 0) {
      return;
    }
    const target = ledgerItems.find((item) => item.id === focusPurchaseId);
    if (!target) {
      return;
    }

    const clear = new URLSearchParams(params);
    clear.delete("focusPurchase");
    setParams(clear, { replace: true });
    setFocusPurchaseId(null);
    void openLedgerDetail(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, focusPurchaseId, ledgerItems]);

  const openLedgerDetail = async (itemId: string) => {
    setLedgerDetailLoadingId(itemId);
    try {
      const detail = await getFinanceLedgerDetail(itemId);
      setLedgerDetail(detail);

      if (!detail) {
        await Swal.fire("Detay bulunamadi", "Bu ledger kaydi icin detay verisi gelmedi.", "warning");
        return;
      }

      await Swal.fire({
        title: "Ledger detayi",
        width: 860,
        html: `<pre style="text-align:left;max-height:60vh;overflow:auto;border:1px solid #e2e8f0;border-radius:12px;padding:12px;background:#f8fafc;font-size:12px;line-height:1.5;">${escapeHtml(
          JSON.stringify(detail, null, 2),
        )}</pre>`,
        confirmButtonText: "Kapat",
      });
    } catch (error) {
      console.error("[admin/finance] ledger detail open failed", error);
      await Swal.fire("Hata", "Ledger detayi acilamadi.", "error");
    } finally {
      setLedgerDetailLoadingId(null);
    }
  };

  const requestReason = async (title: string): Promise<string | null> => {
    const result = await Swal.fire({
      title,
      input: "text",
      inputLabel: "Sebep (zorunlu)",
      inputPlaceholder: "Islem nedeni...",
      inputValidator: (value) => (!value?.trim() ? "Sebep zorunludur." : undefined),
      showCancelButton: true,
      confirmButtonText: "Onayla",
      cancelButtonText: "Vazgec",
    });
    if (!result.isConfirmed) {
      return null;
    }
    return String(result.value ?? "").trim();
  };

  const handleCreateDraft = async (item: CreatorPayoutPreview) => {
    setActionId(item.creatorId);
    try {
      const result = await createCreatorPayout({
        creatorId: item.creatorId,
        from,
        to,
        note: "finance-workspace-draft",
      });

      if (!result.record) {
        const message =
          result.errorMessage ??           "Payout draft olusturulamadi. Secilen aralikta odenebilir tahakkuk olmayabilir.";
        await Swal.fire("Hata", message, "error");
        return;
      }

      await Swal.fire("Basarili", "Payout draft olusturuldu.", "success");
      await loadPayouts();
    } finally {
      setActionId(null);
    }
  };

  const handleApprove = async (record: CreatorPayoutRecord) => {
    const reason = await requestReason("Payout onayi");
    if (!reason) return;

    setActionId(record.id);
    const ok = await approveCreatorPayout(record.id, reason);
    setActionId(null);
    if (!ok) {
      await Swal.fire("Hata", "Payout onayi basarisiz.", "error");
      return;
    }

    await Swal.fire("Onaylandi", "Payout onaylandi.", "success");
    await loadPayouts();
  };

  const handleReject = async (record: CreatorPayoutRecord) => {
    const reason = await requestReason("Payout reddi");
    if (!reason) return;

    setActionId(record.id);
    const ok = await rejectCreatorPayout(record.id, reason);
    setActionId(null);
    if (!ok) {
      await Swal.fire("Hata", "Payout reddi basarisiz.", "error");
      return;
    }

    await Swal.fire("Reddedildi", "Payout kaydi reddedildi.", "success");
    await loadPayouts();
  };

  const handleMarkPaid = async (record: CreatorPayoutRecord) => {
    const reason = await requestReason("Payout odeme");
    if (!reason) return;

    setActionId(record.id);
    const ok = await markCreatorPayoutPaid(record.id, reason);
    setActionId(null);
    if (!ok) {
      await Swal.fire("Hata", "Payout paid islemi basarisiz.", "error");
      return;
    }

    await Swal.fire("Tamamlandi", "Payout paid olarak isaretlendi.", "success");
    await loadPayouts();
  };

  const openPayoutStatement = async (record: CreatorPayoutRecord) => {
    setActionId(record.id);
    try {
      const statement = await getFinancePayoutStatement(record.id);
      if (!statement) {
        await Swal.fire("Detay yok", "Payout statement verisi bulunamadi.", "warning");
        return;
      }

      await Swal.fire({
        title: `Payout Statement · ${statement.creatorName ?? record.creatorName}`,
        width: 960,
        html: renderPayoutStatementHtml(statement),
        confirmButtonText: "Kapat",
      });
    } catch (error) {
      console.error("[admin/finance] payout statement open failed", error);
      await Swal.fire("Hata", "Payout statement acilamadi.", "error");
    } finally {
      setActionId(null);
    }
  };

  useEffect(() => {
    if (tab !== "payout-queue" || !focusPayoutId || records.length === 0 || payoutLoading) {
      return;
    }
    const target = records.find((item) => item.id === focusPayoutId);
    if (!target) {
      return;
    }

    const clear = new URLSearchParams(params);
    clear.delete("focusPayout");
    setParams(clear, { replace: true });
    setFocusPayoutId(null);
    void openPayoutStatement(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, focusPayoutId, records, payoutLoading]);

  return (
    <div className="workspace-canvas space-y-5">
      <header className="workspace-header-card rounded-3xl p-6">
        <div className="inline-flex items-center gap-2">
          <h1 className="workspace-title text-2xl font-bold">Finans Çalışma Alanı</h1>
          <InfoMarkerHint markerKey="finance.payout" />
        </div>
        <p className="workspace-subtitle text-sm">Defter odaklı operasyon merkezi: satış, token, ödeme ve mutabakat.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 max-w-xl">
          <label className="text-xs text-slate-600">
            Baslangic
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600">
            Bitis
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
        </div>
      </header>

      <section className="workspace-tab-list flex flex-wrap gap-2 rounded-2xl p-2">
        {TAB_LIST.map((item) => {
          const active = item.key === tab;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => selectTab(item.key)}
              className={`workspace-tab rounded-full px-4 py-2 text-sm font-semibold transition ${active ? "workspace-tab-active" : ""}`}
            >
              {item.label}
            </button>
          );
        })}
      </section>

      {(tab === "sales-ledger" || tab === "token-ledger") && (
        <section className="workspace-panel rounded-2xl p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-800">{activeLabel}</h2>
            {ledgerLoading && <span className="text-xs text-slate-500">Yükleniyor...</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Tarih</th>
                  <th className="px-3 py-2">Kaynak</th>
                  <th className="px-3 py-2">Kullanici</th>
                  <th className="px-3 py-2">Tutar</th>
                  <th className="px-3 py-2">Durum</th>
                  <th className="px-3 py-2 text-right">Detay</th>
                </tr>
              </thead>
              <tbody>
                {!ledgerLoading && ledgerItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-500">
                      Kayıt bulunamadı.
                    </td>
                  </tr>
                )}
                {ledgerItems.map((item) => (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs text-slate-600">{dayjs(item.occurredAtUtc).format("DD MMM YYYY HH:mm")}</td>
                    <td className="px-3 py-2">{item.sourceType}</td>
                    <td className="px-3 py-2">{item.username || "-"}</td>
                    <td className="px-3 py-2">
                      {(item.currencyAmount ?? 0).toFixed(2)} {item.currencyCode || "ZTN"}
                    </td>
                    <td className="px-3 py-2">{item.status || "-"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => void openLedgerDetail(item.id)}
                        disabled={ledgerDetailLoadingId === item.id}
                        className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-100"
                      >
                        {ledgerDetailLoadingId === item.id ? "Yükleniyor..." : "Detay gör"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ledgerDetail && (
            <pre className="mt-4 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              {JSON.stringify(ledgerDetail, null, 2)}
            </pre>
          )}
        </section>
      )}

      {tab === "creator-accruals" && <AdminRoyalties />}

      {tab === "payout-queue" && (
        <div className="space-y-4">
          <section className="workspace-panel rounded-2xl p-4">
            <div className="inline-flex items-center gap-2">
            <h2 className="text-base font-semibold text-slate-800">Bekleyen Yayıncı Tahakkukları</h2>
              <InfoMarkerHint markerKey="finance.payout" />
            </div>
            <p className="mt-1 text-xs text-slate-500">Önce taslak oluştur, sonra onaylayıp ödendi olarak işaretle.</p>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Yayıncı</th>
                    <th className="px-3 py-2">Brüt</th>
                    <th className="px-3 py-2">Pay</th>
                    <th className="px-3 py-2">Tutar</th>
                    <th className="px-3 py-2 text-right">Taslak</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutLoading && (
                    <tr>
                      <td colSpan={5} className="px-3 py-5 text-center text-sm text-slate-500">
                        Yükleniyor...
                      </td>
                    </tr>
                  )}
                  {!payoutLoading && outstanding.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-5 text-center text-sm text-slate-500">
                        Bekleyen tahakkuk bulunmuyor.
                      </td>
                    </tr>
                  )}
                  {outstanding.map((item) => (
                    <tr key={item.creatorId} className="border-b last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{item.creatorName}</div>
                        <div className="text-xs text-slate-500">{item.creatorEmail || "-"}</div>
                      </td>
                      <td className="px-3 py-2">{item.grossTokenAmount.toFixed(2)} ZTN</td>
                      <td className="px-3 py-2">{item.shareTokenAmount.toFixed(2)} ZTN</td>
                      <td className="px-3 py-2">
                        {item.currencyAmount.toFixed(2)} {item.currencyCode}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => void handleCreateDraft(item)}
                          disabled={actionId === item.creatorId}
                          className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                        >
                          {actionId === item.creatorId ? "..." : "Taslak Oluştur"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="workspace-panel rounded-2xl p-4">
            <h2 className="text-base font-semibold text-slate-800">Ödeme Kayıtları</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Creator</th>
                    <th className="px-3 py-2">Donem</th>
                    <th className="px-3 py-2">Pay</th>
                    <th className="px-3 py-2">Durum</th>
                    <th className="px-3 py-2 text-right">Aksiyon</th>
                  </tr>
                </thead>
                <tbody>
                  {!payoutLoading && records.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-5 text-center text-sm text-slate-500">
                        Kayıt bulunamadı.
                      </td>
                    </tr>
                  )}
                  {records.map((record) => {
                    const badgeClass = STATUS_BADGE[String(record.status || "").toLowerCase()] || "border-slate-300 bg-slate-50 text-slate-700";
                    const isPending = String(record.status).toLowerCase() === "pending";
                    const isApproved = String(record.status).toLowerCase() === "approved";
                    return (
                      <tr key={record.id} className="border-b last:border-0">
                        <td className="px-3 py-2">{record.creatorName}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {dayjs(record.periodStartUtc).format("DD MMM")} - {dayjs(record.periodEndUtc).format("DD MMM")}
                        </td>
                        <td className="px-3 py-2">
                          {record.shareTokenAmount.toFixed(2)} ZTN / {record.currencyAmount.toFixed(2)} {record.currencyCode}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${badgeClass}`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex flex-wrap items-center justify-end gap-2">
                            {isPending && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => void handleApprove(record)}
                                  disabled={actionId === record.id}
                                  className="rounded border border-blue-300 px-3 py-1 text-xs text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                                >
                                  Onayla
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleReject(record)}
                                  disabled={actionId === record.id}
                                  className="rounded border border-rose-300 px-3 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                >
                                  Reddet
                                </button>
                              </>
                            )}
                            {isApproved && (
                              <button
                                type="button"
                                onClick={() => void handleMarkPaid(record)}
                                disabled={actionId === record.id}
                                className="rounded border border-emerald-300 px-3 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                              >
                                Ödendi
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => void openPayoutStatement(record)}
                              disabled={actionId === record.id}
                              className="rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                            >
                              Döküm
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {tab === "reconciliation" && (
        <section className="workspace-panel rounded-2xl p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-800">Mutabakat</h2>
            {reconLoading && <span className="text-xs text-slate-500">Yükleniyor...</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Tarih</th>
                  <th className="px-3 py-2">Tip</th>
                  <th className="px-3 py-2">Durum</th>
                  <th className="px-3 py-2">Severity</th>
                  <th className="px-3 py-2">Detay</th>
                </tr>
              </thead>
              <tbody>
                {!reconLoading && reconItems.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-500">
                      Mutabakat sorunu bulunmuyor.
                    </td>
                  </tr>
                )}
                {reconItems.map((item) => (
                  <tr key={item.id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs text-slate-600">{dayjs(item.occurredAtUtc).format("DD MMM YYYY HH:mm")}</td>
                    <td className="px-3 py-2">{item.issueType}</td>
                    <td className="px-3 py-2">{item.status}</td>
                    <td className="px-3 py-2">{item.severity}</td>
                    <td className="px-3 py-2">{item.detail || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function renderPayoutStatementHtml(statement: FinancePayoutStatement): string {
  const header = `
    <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin-bottom:12px;text-align:left;font-size:12px;">
      <div><b>Creator:</b> ${escapeHtml(statement.creatorName ?? "-")}</div>
      <div><b>Status:</b> ${escapeHtml(statement.status ?? "-")}</div>
      <div><b>Period:</b> ${escapeHtml(dayjs(statement.periodStartUtc).format("DD MMM YYYY"))} - ${escapeHtml(dayjs(statement.periodEndUtc).format("DD MMM YYYY"))}</div>
      <div><b>Share:</b> ${Number(statement.shareTokenAmount ?? 0).toFixed(2)} ZTN (${Number(statement.sharePercent ?? 0).toFixed(2)}%)</div>
      <div><b>Currency:</b> ${Number(statement.currencyAmount ?? 0).toFixed(2)} ${escapeHtml(statement.currencyCode ?? "TRY")}</div>
      <div><b>Ref:</b> ${escapeHtml(statement.paymentReference ?? "-")}</div>
    </div>`;

  const breakdownRows = (statement.sourceBreakdown ?? [])
    .map(
      (item) => `
        <tr>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;">${escapeHtml(item.sourceType ?? "-")}</td>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${Number(item.entryCount ?? 0)}</td>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${Number(item.shareTokenAmount ?? 0).toFixed(2)}</td>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${Number(item.currencyAmount ?? 0).toFixed(2)}</td>
        </tr>`,
    )
    .join("");

  const entries = statement.entries ?? [];
  const entryPreview = entries
    .slice(0, 25)
    .map(
      (entry) => `
        <tr>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;">${escapeHtml(dayjs(entry.occurredAtUtc).format("DD MMM HH:mm"))}</td>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;">${escapeHtml(entry.sourceType ?? "-")}</td>
          <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${Number(entry.shareTokenAmount ?? 0).toFixed(2)}</td>
        </tr>`,
    )
    .join("");

  return `
    ${header}
    <div style="text-align:left;margin:8px 0 6px;font-weight:600;">Kaynak kırılımı</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:10px;">
      <thead>
        <tr>
          <th style="padding:6px;text-align:left;border-bottom:1px solid #cbd5e1;">Kaynak</th>
          <th style="padding:6px;text-align:right;border-bottom:1px solid #cbd5e1;">Adet</th>
          <th style="padding:6px;text-align:right;border-bottom:1px solid #cbd5e1;">Pay (ZTN)</th>
          <th style="padding:6px;text-align:right;border-bottom:1px solid #cbd5e1;">Tutar</th>
        </tr>
      </thead>
      <tbody>${breakdownRows || '<tr><td colspan="4" style="padding:8px;text-align:center;color:#64748b;">Kayıt yok</td></tr>'}</tbody>
    </table>
    <div style="text-align:left;margin:8px 0 6px;font-weight:600;">Entry önizleme (ilk 25)</div>
    <div style="max-height:260px;overflow:auto;border:1px solid #e2e8f0;border-radius:8px;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr>
            <th style="padding:6px;text-align:left;border-bottom:1px solid #cbd5e1;">Tarih</th>
            <th style="padding:6px;text-align:left;border-bottom:1px solid #cbd5e1;">Kaynak</th>
            <th style="padding:6px;text-align:right;border-bottom:1px solid #cbd5e1;">Pay</th>
          </tr>
        </thead>
        <tbody>${entryPreview || '<tr><td colspan="3" style="padding:8px;text-align:center;color:#64748b;">Kayıt yok</td></tr>'}</tbody>
      </table>
    </div>
  `;
}
